import { runInAction, makeAutoObservable } from 'mobx';
import RootStore from './RootStore';

const defaultRegNewUser = {
  email: '',
  firstName: '',
  lastName: '',
  department: 'D&E',
  team: 'None',
  password: '',
};
type RegNewUserKey = keyof typeof defaultRegNewUser;

class RegistrationStore {
  rootStore;
  loading = false;
  errors = {
    isError: false,
    message: '',
    messages: [],
    name: '',
  };

  departments = ['D&E', 'Plant', 'Project-Management', 'Estimating'].sort();

  teams = [
    'None',
    ...[
      'Carpenter',
      'Casting',
      'Handling/Finishing',
      'Q/A',
      'Rebar',
      'Weld',
    ].sort(),
  ];

  registrationReady = false;
  regNewUser = defaultRegNewUser;
  confirmationPassword = '';
  newlyRegUser = '';

  /**
   * @constructor constructor for registration class, initializes stores
   * and feather services
   */
  constructor(rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false });
    this.rootStore = rootStore;
  }

  handleConfirmationPassword = (value: string) => {
    this.confirmationPassword = value;
  };

  handleNewUser = (key: RegNewUserKey, value: string) => {
    this.regNewUser[key] = value;
  };

  /**
   * @function clearErrors
   */
  clearErrors = () => {
    this.errors = { isError: false, message: '', messages: [], name: '' };
  };

  /**
   * @function handleErrors
   * @param err
   */
  handleErrors = (err: Error & { code?: number }) => {
    console.log('error', err);
    if (err.code === 400) {
      this.rootStore.notificationStore?.addNotification({
        message: `Error registering${err.message ? `: ${err.message}` : ''}`,
        options: { variant: 'error', autoHideduration: 4000 },
      });
    }
    if (err.code === 401 && err.message === 'Invalid login') {
      this.rootStore.notificationStore?.addNotification({
        message: `Error registering${err.message ? `: ${err.message}` : ''}`,
        options: { variant: 'error', autoHideduration: 4000 },
      });
    }
    this.loading = false;
  };

  signupKeypress = (event: { key: string }) => {
    if (
      event.key === 'Enter' &&
      !this.disablePasswordSubmit &&
      !this.disableNameSubmit &&
      !this.disableEmailSubmit
    ) {
      this.completeRegistration();
    }
  };

  completeRegistration = async () => {
    this.loading = true;
    try {
      const email = this.regNewUser?.email?.includes('@intrasyncindustrial.com')
        ? this.regNewUser.email
        : `${this.regNewUser.email}@intrasyncindustrial.com`;
      const newUser = {
        ...this.regNewUser,
        email: email,
      };
      // const response = await this.rootStore?.userStore?.create(newUser, {
      //   query: { appName: this.rootStore.appName || '@intrasync/web' },
      // });

      const response = await this.rootStore?.userStore?.create(newUser);
      runInAction(async () => {
        if (response) {
          this.regNewUser = defaultRegNewUser;
          console.log('goTo in registrationStore');
          return this.rootStore.routerStore?.goTo(
            'LoginPage',
            {},
            { checkEmailPrompt: true }
          );
        }
        this.loading = false;
      });
      return response;
    } catch (err) {
      this.handleErrors(err as Error);
    }
  };

  get userAlreadyExists() {
    const entities =
      (this.rootStore.userStore?.entities as { email: string }[]) || [];
    if (!this.regNewUser.email) return false;
    return entities
      .slice()
      .some(
        (user: { email: string }) =>
          user?.email?.replace('@intrasyncindustrial.com', '') ===
          this.regNewUser?.email?.replace('@intrasyncindustrial.com', '')
      );
  }

  get emailTooShort() {
    if (this.regNewUser?.email?.length < 3) {
      return true;
    }
    return false;
  }

  get disableEmailSubmit() {
    if (this.userAlreadyExists) {
      return true;
    }
    if (this.emailTooShort) {
      return true;
    }

    return false;
  }

  get firstNameTooShort() {
    if (this.regNewUser?.firstName?.length < 3) {
      return true;
    }
    return false;
  }

  get lastNameTooShort() {
    if (this.regNewUser?.lastName?.length < 3) {
      return true;
    }
    return false;
  }

  get disableNameSubmit() {
    if (this.firstNameTooShort || this.lastNameTooShort) {
      return true;
    }
    return false;
  }

  get passwordTooShort() {
    if (this.regNewUser?.password?.length < 3) {
      return true;
    }
    return false;
  }

  get passwordsMatch() {
    if (this.regNewUser.password !== this.confirmationPassword) return false;
    return true;
  }

  get disablePasswordSubmit() {
    if (this.passwordTooShort || !this.passwordsMatch) {
      return true;
    }
    return false;
  }
}

export default RegistrationStore;
